/**
 * ImageManager Component
 *
 * Complete image management panel for templates
 * Combines ImageUploader and ImageLibrary into a single interface
 */

import { Stack, ScrollArea, Divider, Text } from "@mantine/core";
import { ImageLibrary } from "./ImageLibrary";
import { ImageUploader } from "./ImageUploader";
import { useTemplateImages } from "../../hooks/useTemplateImages";
import type { TemplateImage } from "../../types/template";

export interface ImageManagerProperties {
  /** Template ID */
  templateId: string;
  /** Current images from template */
  currentImages?: TemplateImage[];
  /** Callback when a variable is copied (optional - for inserting into editor) */
  onVariableCopy?: (variable: string) => void;
  /** Max height for scroll area */
  maxHeight?: number;
}

/**
 * ImageManager - Complete image management for templates
 *
 * Features:
 * - Add images via URL or upload
 * - View all template images in a grid
 * - Copy image variables to clipboard
 * - Remove images (with Storage cleanup)
 * @param root0
 * @param root0.templateId
 * @param root0.currentImages
 * @param root0.onVariableCopy
 * @param root0.maxHeight
 */
export function ImageManager({
  templateId,
  currentImages = [],
  onVariableCopy,
  maxHeight = 500
}: ImageManagerProperties) {
  const { addImageUrl, uploadImage, removeImage, isAddingUrl, isUploading, isRemoving } = useTemplateImages({
    templateId,
    currentImages
  });

  const existingNames = currentImages.map((img) => img.nome);

  return (
    <Stack gap="md" h="100%">
      <Text size="sm" c="dimmed">
        Adicione imagens para usar como fundo nas páginas do relatório. Use a variável copiada no HTML do template.
      </Text>

      <ImageUploader
        onAddUrl={addImageUrl}
        onUpload={uploadImage}
        isAddingUrl={isAddingUrl}
        isUploading={isUploading}
        existingNames={existingNames}
      />

      <Divider label="Imagens Adicionadas" labelPosition="center" />

      <ScrollArea h={maxHeight} offsetScrollbars>
        <ImageLibrary
          images={currentImages}
          onRemove={removeImage}
          isRemoving={isRemoving}
          onVariableCopy={onVariableCopy}
        />
      </ScrollArea>
    </Stack>
  );
}
